import { useQuery } from "@tanstack/react-query";
import {
  fetchAliasCandidates,
  fetchBrandCoverage,
  fetchBrands,
  fetchDiscoveredCompanies,
  fetchReports,
  fetchRuns,
  type Report,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";
import { isUnfinished } from "../unfinished-report.js";

/** Brands, reports, and the companies an answer named. */

export function useRuns(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.runs(workspaceId),
    queryFn: () => fetchRuns(workspaceId),
    enabled,
  });
}

export function useBrands(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.brands(workspaceId),
    queryFn: async () => await fetchBrands(workspaceId),
    enabled,
  });
}

export function useBrandCoverage(workspaceId: string, brandId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.brandCoverage(workspaceId, brandId),
    queryFn: () => fetchBrandCoverage(workspaceId, brandId),
    enabled,
  });
}

const finished = (reports: readonly Report[]): readonly Report[] =>
  reports.filter((report) => !isUnfinished(report));

/**
 * The workspace's brand reports — those whose setup is finished, the only ones
 * a person can open, read or put a question on.
 *
 * A setup still in progress is a report too, but it belongs to onboarding,
 * which asks for it with `useReportsWithSetups`. Until 2026-10-13 every caller
 * got both and had to remember to drop the setups; the rail did, and the
 * Library's "Add to brand report" did not, so it offered two abandoned setups
 * that looked deleted everywhere else.
 */
export function useReports(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.reports(workspaceId),
    queryFn: () => fetchReports(workspaceId),
    enabled,
    select: finished,
  });
}

/** Every report, setups still in progress among them: onboarding's view. */
export function useReportsWithSetups(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.reports(workspaceId),
    queryFn: () => fetchReports(workspaceId),
    enabled,
  });
}

export function useDiscoveredCompanies(
  workspaceId: string,
  reportId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.discoveredCompanies(workspaceId, reportId),
    queryFn: () => fetchDiscoveredCompanies(workspaceId, reportId),
    enabled,
  });
}

export function useAliasCandidates(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.aliasCandidates(workspaceId),
    queryFn: () => fetchAliasCandidates(workspaceId),
    enabled,
  });
}
